import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import TextInput from '../common/InputWithLabel';
import Select2 from '../common/Select2';
import useInput from '../../hooks/useInput';
import useSelect2 from '../../hooks/useSelect2';
import SubmitButton from '../common/PrimaryButton';
import StartApplicationValidator from '../../helpers/validators/start-application.validator';
import findErrorField from '../../helpers/serverError';
import Error from '../common/Error';
import Normalizer from '../../helpers/normalizers';
import { BACKEND_CONFIG } from '../../config/consts';

import Typeahead from './Typeahead';
import style from './StartApplicationForm.module.css';

const StartApplicationForm = React.memo(({ model, error = '' }) => {
  const [generalError, setGeneralError] = useState(error);
  const producer = useSelect2(model.Producer);
  const csr = useSelect2(model.CSR);
  const branch = useSelect2(model.Branch);
  const profitCenter = useSelect2(model.ProfitCenter);
  const code = useInput(model.EPICClientCode || '');
  const industry = useSelect2(model.Industry);
  const profession = useSelect2(model.Profession);
  const [categories, setCategories] = useState(null);
  const [isTypeaheadEnabled, setIsTypeaheadEnabled] = useState(
    BACKEND_CONFIG.get('startApplicationPageComponent') === 'Typeahead',
  );

  const handleBackendConfigChange = () => {
    setIsTypeaheadEnabled(
      BACKEND_CONFIG.get('startApplicationPageComponent') === 'Typeahead',
    );
  };

  useEffect(() => {
    axios
      .request({
        url: 'start-application/categories',
        method: 'get',
        baseURL: '/api/primary_hubagencyportal/',
      })
      .then(response => {
        setCategories(response.data.IndustriesServices);
      });

    window.addEventListener(
      'backendConfigChange',
      handleBackendConfigChange,
      false,
    );

    return () => {
      window.removeEventListener(
        'backendConfigChange',
        handleBackendConfigChange,
      );
    };
  }, []);

  useEffect(() => {
    const pr = model.Producers?.find(
      item => item.value.toString() === model.Producer.toString(),
    );
    if (pr) {
      producer.setValue({
        data: null,
        label: pr.label,
        value: pr.value,
      });
    }

    const cs = model.CSRs?.find(
      item => item.value.toString() === model.CSR.toString(),
    );
    if (cs) {
      csr.setValue({
        data: null,
        label: cs.label,
        value: cs.value,
      });
    }

    const pc = model.ProfitCentres?.find(
      item => item.value.toString() === model.ProfitCenter?.toString(),
    );
    if (pc) {
      profitCenter.setValue({
        data: null,
        label: pc.label,
        value: pc.value,
      });
    }

    const br = model.Branches?.find(
      item =>
        model.Branch && item.value.toString() === model.Branch?.toString(),
    );
    if (br) {
      branch.setValue({
        data: null,
        label: br.label,
        value: br.value,
      });
    }

    if (model.EPICClientCode) {
      code.setValue(model.EPICClientCode);
    }

    const ind =
      model.Industries &&
      model.Industries.find(
        item =>
          model.Industry && item.value.toString() === model.Industry.toString(),
      );
    if (ind) {
      industry.setValue({
        data: null,
        label: ind.label,
        value: ind.value,
      });

      const prof = model.Professions?.find(
        item =>
          parseInt(item.value, 10).toString() === model.Profession?.toString(), // Use leading integer of item.value to match
      );
      if (prof) {
        profession.setValue({
          data: null,
          label: prof.label,
          value: prof.value,
        });
      }
    }
  }, [branch, code, csr, industry, model, producer, profession, profitCenter]);

  const professions =
    industry.value && industry.value.value
      ? (model.Professions || []).filter(item => {
          return item.data.industryId === industry.value.value;
        })
      : [];

  const [errors, setErrors] = useState({
    producer: '',
    csr: '',
    branch: '',
    profitCenter: '',
    code: '',
    industry: '',
    profession: '',
  });

  const handleSubmit = event => {
    event.preventDefault();

    const validation = StartApplicationValidator.validate({
      producer: producer.value,
      csr: csr.value,
      branch: branch.value,
      profitCenter: profitCenter.value,
      code: code.value,
      industry: industry.value,
      profession: profession.value,
    });

    setErrors({
      producer: validation.producer.message,
      csr: validation.csr.message,
      branch: validation.branch.message,
      profitCenter: validation.profitCenter.message,
      code: validation.code.message,
      industry: validation.industry.message,
      profession: validation.profession.message,
    });

    const handleError = ({ response }) => {
      if (response && response.data && response.data.ValidationErrors) {
        const producerError = findErrorField(
          response.data.ValidationErrors,
          'Producer',
        );
        const csrError = findErrorField(response.data.ValidationErrors, 'CSR');
        const branchError = findErrorField(
          response.data.ValidationErrors,
          'Branch',
        );
        const profitCenterError = findErrorField(
          response.data.ValidationErrors,
          'ProfitCenter',
        );
        const codeError = findErrorField(
          response.data.ValidationErrors,
          'EPICClientCode',
        );
        const industryError = findErrorField(
          response.data.ValidationErrors,
          'Industry',
        );
        const professionError = findErrorField(
          response.data.ValidationErrors,
          'Profession',
        );
        setErrors({
          producer: producerError && producerError.Messages[0],
          csr: csrError && csrError.Messages[0],
          branch: branchError && branchError.Messages[0],
          profitCenter: profitCenterError && profitCenterError.Messages[0],
          code: codeError && codeError.Messages[0],
          industry: industryError && industryError.Messages[0],
          profession: professionError && professionError.Messages[0],
        });
      } else {
        setGeneralError('Something went wrong. Please try again.');
      }
    };

    const handleSuccess = ({ data }) => {
      switch (data.status) {
        case 'complete':
          window.location = data.message;
          break;
        default:
          setGeneralError(data.message);
          break;
      }
    };

    const professionId = parseInt(
      profession.value && profession.value.value,
      10,
    ).toString(); // Extract leading integer id

    if (validation.isValid) {
      axios
        .post(`${process.env.REACT_APP_BASE_URL}hub/applicationinitiations`, {
          Producer: producer.value.value,
          CSR: csr.value.value,
          Branch: branch.value.value,
          ProfitCenter: profitCenter.value.value,
          EPICClientCode: code.value,
          Industry: industry.value.value,
          Profession: professionId,
          ForProfit: model.ForProfit,
        })
        .then(handleSuccess)
        .catch(handleError);
    }
  };

  const handleSelect = webBusClassIdDescription => {
    const webBusClassId = webBusClassIdDescription.split('|')[0];
    const webBusClassIdNumber = Number(webBusClassId);

    const category = categories.find(cat =>
      cat.Services.find(service => service.Id === webBusClassIdNumber),
    );

    industry.setValue({
      data: null,
      label: 'industry',
      value: String(category.Id),
    });
    profession.setValue({
      data: null,
      label: 'profession',
      value: webBusClassId,
    });
  };

  const filteredProducers =
    model.Producers &&
    model.Producers.filter(
      producer_ => !producer_.label.toLowerCase().startsWith('disable'),
    );
  const filteredCSRs =
    model.CSRs &&
    model.CSRs.filter(csr_ => !csr_.label.toLowerCase().startsWith('disable'));

  if (isTypeaheadEnabled === null) {
    return null;
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className={style.startApp}>
        <Select2
          label="HUB Producer"
          placeholder="Choose Producer"
          {...producer}
          error={errors.producer}
          initialValue={`${producer.value && producer.value.value}`}
          options={filteredProducers || []}
        />
        <Select2
          label="HUB Account Manager"
          placeholder="Choose Account Manager"
          {...csr}
          error={errors.csr}
          initialValue={`${csr.value && csr.value.value}`}
          options={filteredCSRs || []}
        />
        <Select2
          label="Branch"
          placeholder="Choose Branch"
          {...branch}
          error={errors.branch}
          options={model.Branches || []}
          initialValue={`${branch.value && branch.value.value}`}
        />
        <Select2
          label="Profit Center"
          placeholder="Choose Profit Center"
          {...profitCenter}
          error={errors.profitCenter}
          options={model.ProfitCentres || []}
          initialValue={`${profitCenter.value && profitCenter.value.value}`}
        />
        <TextInput
          id="code"
          name="code"
          label="EPIC Client Code"
          placeholder="Enter 10-digit client code"
          autocomplete="off"
          {...code}
          value={Normalizer.extractAllButSpaces(code.value)}
          error={errors.code}
        />
        {isTypeaheadEnabled ? (
          <>
            <div className={style.businessTypeTitle}>Business Type</div>
            <div className={style.businessTypeNote}>
              Please note — If you do not see a Business Type that accurately
              describes the Insured’s business, please select “standard
              application“
            </div>
            <Typeahead
              data-testid="industrytypeahead"
              dropdownClassName="homepage-typeahead"
              onSelect={handleSelect}
            />
          </>
        ) : (
          <>
            <Select2
              label="Industry"
              placeholder="Choose Industry"
              {...industry}
              error={errors.industry}
              options={model.Industries || []}
              initialValue={`${industry.value && industry.value.value}`}
            />
            <Select2
              label="Profession"
              placeholder="Choose Profession"
              {...profession}
              error={errors.profession}
              options={professions}
              initialValue={`${profession.value && profession.value.value}`}
            />
          </>
        )}
        {generalError && <Error error={generalError} />}
      </div>
      <SubmitButton text="Continue" />
    </form>
  );
});

StartApplicationForm.propTypes = {
  model: PropTypes.shape({
    Producer: PropTypes.number,
    CSR: PropTypes.number,
    Branch: PropTypes.string,
    ProfitCenter: PropTypes.string,
    EPICClientCode: PropTypes.string,
    Industry: PropTypes.number,
    Profession: PropTypes.number,
  }).isRequired,
  error: PropTypes.string,
};

export default StartApplicationForm;
